import { useCallback, useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";
import { useParams } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import PasswordField from "components/password-field";
import {
  resetUserPasswordConfirm,
  selectAuthErrorMessage,
  selectAuthIsFetching,
  selectAuthIsSuccess
} from "features/auth/auth-slice";

import PasswordResetConfirmation from "./password-reset-confirmation.component";

interface IFormInput {
  password: string;
}

export default function PasswordReset() {
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const { uid, token } = useParams();
  const errorMessage = useAppSelector(selectAuthErrorMessage);
  const isFetching = useAppSelector(selectAuthIsFetching);
  const isSuccess = useAppSelector(selectAuthIsSuccess);
  const [open, setOpen] = useState(false);

  const { control, handleSubmit } = useForm<IFormInput>({
    mode: "onBlur",
    defaultValues: { password: "" }
  });

  const { isValid } = useFormState({ control });

  useEffect(() => {
    if (isSuccess) setOpen(true);
  }, [isSuccess]);

  const onSubmit: SubmitHandler<IFormInput> = ({ password }) => {
    dispatch(
      resetUserPasswordConfirm({
        password,
        uid,
        token
      })
    );
  };

  const handleOnBlur = useCallback(() => {
    if (submitButtonRef.current) submitButtonRef.current.focus();
  }, []);

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          {Boolean(errorMessage) && (
            <Alert icon={false} severity="error">
              {errorMessage}
            </Alert>
          )}
          <Box>
            <Typography variant="h3" gutterBottom component="div">
              Create a New Password
            </Typography>
            <Typography variant="subtitle1" fontSize={17} component="div">
              Please enter a new password to reset your account.
            </Typography>
          </Box>
          <PasswordField
            name="password"
            label="Password"
            control={control}
            onBlur={handleOnBlur}
            validatePassword
          />
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              loading={isFetching}
              disabled={!isValid}
            >
              Submit
            </LoadingButton>
          </Box>
        </Stack>
        <Box marginTop={4} marginLeft={8.5} marginRight={8} marginBottom={9}>
          <FormHelperText>
            Once you've reset your password, you'll be able to sign in to your
            account with your new credentials.
          </FormHelperText>
        </Box>
      </Box>
      <PasswordResetConfirmation open={open} onClose={() => setOpen(false)} />
    </>
  );
}
